import { ReactNode, useState } from 'react';
import {
  QueryClient, QueryClientProvider,
  DefaultOptions, QueryCache,
  MutationCache,
} from '@tanstack/react-query';
import { useToast, ToastType, ToastContextType } from '@/contexts/ToastContext';
import { getErrorMessage } from '@/utils/helpers/i18nHelper';
import { isNonRetryableApiError } from '@/lib/auth/apiErrors';
import { isUsageTimeLimitError } from '@/lib/auth/usageTimeLimitLockout';

interface QueryClientProviderWrapperProps {
  children: ReactNode;
}

const shouldRetry = (failureCount: number, error: unknown): boolean => {
  if (isNonRetryableApiError(error)) return false;
  return failureCount < 3;
};

const queryConfig = {
  queries: {
    throwOnError: false,
    refetchOnWindowFocus: false,
    retry: shouldRetry,
    staleTime: 1000 * 60,
  },
  mutations: {
    throwOnError: false,
    retry: shouldRetry,
  },
} satisfies DefaultOptions;

const GetQueryCache = (toastNotify: ToastContextType['toastNotify']) => new QueryCache({
  onError: (error) => {
    if (isUsageTimeLimitError(error)) return;
    const message = getErrorMessage(error);
    toastNotify(ToastType.ERROR, message);
  },
});

const GetMutationCache = (toastNotify: ToastContextType['toastNotify']) => new MutationCache({
  onError: (error) => {
    if (isUsageTimeLimitError(error)) return;
    const message = getErrorMessage(error);
    toastNotify(ToastType.ERROR, message);
  },
});

const QueryClientProviderWrapper = ({ children }: QueryClientProviderWrapperProps) => {
  const { toastNotify } = useToast();
  // Keep QueryClient stable across re-renders (including i18n language changes)
  const [queryClient] = useState(() => new QueryClient({
    defaultOptions: queryConfig,
    queryCache: GetQueryCache(toastNotify),
    mutationCache: GetMutationCache(toastNotify),
  }));

  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  );
};

export default QueryClientProviderWrapper;
